import { useRef, useState } from "react";
import { Upload, Loader2, X, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

// FileUpload — drag/drop + progress.
// Props:
//   kind        — upload endpoint kind (music_on_hold | audio_prompts | lead_lists | dnc_lists | voicemail)
//   value       — current uploaded URL string (optional)
//   onChange    — receives the new URL after successful upload
//   accept      — input accept attribute
//   testId      — for data-testid
//   label       — caption
//
export default function FileUpload({ kind, value, onChange, accept = "audio/*", testId = "file-upload", label = "Upload file" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  async function doUpload(file) {
    if (!file) return;
    setUploading(true); setProgress(0);
    const form = new FormData(); form.append("file", file);
    try {
      const r = await api.post(`/admin/uploads/${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || file.size)) * 100)),
      });
      onChange?.(r.data?.url || "", r.data);
      toast.success(`Uploaded ${r.data?.original || file.name}`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setUploading(false); setProgress(0); }
  }

  function onDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) doUpload(f);
  }

  function onPick(e) {
    const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = "";
  }

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      data-testid={testId}
      className="border border-dashed border-border rounded-lg p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
    >
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={onPick} data-testid={`${testId}-input`} />
      {value ? (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-emerald-500/15 grid place-items-center">
            <FileCheck2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{value.split("/").pop()}</div>
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Open</a>
          </div>
          <Button data-testid={`${testId}-clear`} onClick={() => onChange?.("")} variant="ghost" size="icon" className="h-8 w-8 text-destructive"><X className="w-4 h-4" /></Button>
          <Button data-testid={`${testId}-replace`} onClick={() => inputRef.current?.click()} variant="outline" size="sm">Replace</Button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-muted grid place-items-center"><Upload className="w-4 h-4 text-muted-foreground" /></div>
          <div className="flex-1">
            <div className="text-sm font-medium">{label}</div>
            <div className="text-xs text-muted-foreground">Drag &amp; drop or click to browse. {accept}</div>
            {uploading && <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} /></div>}
          </div>
          <Button data-testid={`${testId}-browse`} onClick={() => inputRef.current?.click()} disabled={uploading} variant="outline">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Choose"}
          </Button>
        </div>
      )}
    </div>
  );
}
